/**
 * Validates a template string before it's processed by parseExpressions.
 *
 * @remarks
 * This function now correctly handles templates with text and operators outside
 * of the expression blocks (e.g., "Result: ${fieldA} - ${fieldB}").
 * It validates:
 * 1. Content INSIDE each ${...} block for valid expressions.
 * 2. Content OUTSIDE each ${...} block for invalid syntax characters.
 * 3. Existence of all referenced fields.
 *
 * @param formula The template string input by the user.
 * @param availableFields An array of strings representing all valid top-level field names.
 * @returns A string containing an error message if validation fails, otherwise `null`.
 */
export const validateFormula = (formula: string, availableFields: string[]): string | null => {
  if (!formula) return null

  // --- CHECK 1: Basic Syntax (Brackets) ---
  const openBrackets = (formula.match(/\${/g) || []).length
  const closeBrackets = (formula.match(/}/g) || []).length
  if (openBrackets !== closeBrackets) {
    return 'Syntax error: Unbalanced brackets. Please ensure every ${ is matched with a }.'
  }
  if (/\${}/.test(formula)) {
    return 'Syntax error: Empty expression ${} is not allowed.'
  }

  // --- CHECK 2: Content OUTSIDE the blocks ---
  // Split the formula by the ${...} blocks to get the text in between.
  const outsideParts = formula.split(/\${[^}]+}/g)
  for (const part of outsideParts) {
    // Text outside the blocks should not contain '{' or '}'
    if (/[{}]/.test(part)) {
      return `Syntax error: Invalid character '{' or '}' found outside an expression block.`
    }
  }

  // --- CHECK 3: Content INSIDE each block ---
  const fieldReferences = new Set<string>()
  const expressionRegex = /\${([^}]+)}/g
  let match

  while ((match = expressionRegex.exec(formula)) !== null) {
    const content = match[1] // The content inside ${...}

    // A. Check for invalid characters within this specific block.
    const invalidCharPattern = /[^a-zA-Z0-9_\.\s\+\-\*\/\%\(\)]/
    if (invalidCharPattern.test(content)) {
      return `Invalid character in expression "${content}". Only letters, numbers, and operators (+, -, *, /, %, .) are allowed.`
    }

    // B. Collect field references for existence check.
    const tokens = content.match(/[a-zA-Z0-9_\.]+/g) || []
    for (const token of tokens) {
      if (!isNaN(Number(token))) {
        continue // It's a number, not a field.
      }
      const topLevelField = token.split('.')[0]
      if (topLevelField) {
        fieldReferences.add(topLevelField)
      }
    }
  }

  // --- CHECK 4: Field Existence ---
  const missingFields = Array.from(fieldReferences).filter((field) => !availableFields.includes(field))

  if (missingFields.length > 0) {
    const baseMessage = `The following field${
      missingFields.length > 1 ? 's are' : ' is'
    } not recognized: ${missingFields.join(', ')}.`
    const availableFieldsHint =
      availableFields.length > 0 ? ` Available fields are: ${availableFields.join(', ')}.` : ''
    return baseMessage + availableFieldsHint
  }

  return null // All checks passed
}
